// Dashboards made from a device page (VisionPlatform device-dashboards DDB-28, WEB-DSH-DEV): the page's widgets,
// already bound to the unit (no `$self` left, profile.ts bindSelf), copied into a plain dashboard document.
import type { CreavisionDashboardDocument } from "../../documents/generated/dashboard";
import type { Widget } from "../../widgets/types";

export type DashboardDocument = CreavisionDashboardDocument;
type DocWidget = DashboardDocument["widgets"][number];

/** DSH-01: at most 40 widgets on a dashboard. */
export const MAX_WIDGETS = 40;

/** A page widget as a dashboard widget: the document's own keys only, and never `$self` (DSH-V-04). */
export function toDocWidget(w: Widget, id = w.id): DocWidget {
  if (JSON.stringify(w).includes('"$self"')) throw new Error("a dashboard never contains $self (DSH-V-04)");
  return {
    id,
    type: w.type as DocWidget["type"],
    position: { ...w.position },
    ...(w.title ? { title: w.title } : {}),
    ...(w.bindings ? { bindings: w.bindings.map((b) => ({ ...b })) } : {}),
    ...(w.source ? { source: { ...w.source } } : {}),
    ...(w.automation ? { automation: w.automation } : {}),
    ...(w.config && Object.keys(w.config).length ? { config: { ...w.config } } : {}),
  };
}

/** Create dashboard from this device (WEB-DSH-DEV-01): the page's grid as a new, plain dashboard. */
export function fromDevicePage(name: string, widgets: Widget[]): DashboardDocument {
  return { schema: 1, name, columns: 12, widgets: widgets.slice(0, MAX_WIDGETS).map((w) => toDocWidget(w)) };
}

/** An id not yet in the document, from the widget's own. */
function freeId(doc: DashboardDocument, base: string): string {
  const taken = new Set(doc.widgets.map((w) => w.id));
  if (!taken.has(base)) return base;
  for (let i = 2; ; i++) if (!taken.has(`${base}-${i}`)) return `${base}-${i}`;
}

/**
 * Add to dashboard (WEB-DSH-DEV-02): the widget below the dashboard's last one, at the left, keeping its size.
 * Undefined when the dashboard is full.
 */
export function appendWidget(doc: DashboardDocument, w: Widget): DashboardDocument | undefined {
  if (doc.widgets.length >= MAX_WIDGETS) return undefined;
  const bottom = Math.max(0, ...doc.widgets.map((x) => x.position.y + x.position.h));
  const copy = toDocWidget(w, freeId(doc, w.id));
  copy.position = { x: 0, y: bottom, w: Math.min(12, w.position.w), h: w.position.h };
  return { ...doc, widgets: [...doc.widgets, copy] };
}

/** The stored document as a dashboard document, or undefined when it is not one (the API keeps it opaque). */
export function asDocument(v: unknown): DashboardDocument | undefined {
  if (v === null || typeof v !== "object") return undefined;
  const d = v as Partial<DashboardDocument>;
  return d.schema === 1 && typeof d.name === "string" && Array.isArray(d.widgets)
    ? (d as DashboardDocument)
    : undefined;
}
